import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { MessageSquare, Send, Eye, EyeOff, Trash2 } from "lucide-react";
import { listarComentarios, criarComentario, deletarComentario } from "../../services/comentarios";
import { useAuth } from "../../hooks/useAuth";
import { useConfirm } from "../../components/ConfirmProvider";
import { renderTextoRico } from "../../utils/textoComLinks";

export function ProjetoComentariosSection({ projetoId }: { projetoId: string }) {
  const { user, can } = useAuth();
  const qc = useQueryClient();
  const confirm = useConfirm();
  const [texto, setTexto] = useState("");
  const [visivelParaCliente, setVisivelParaCliente] = useState(true);
  // Comentários longos ficam recolhidos (line-clamp) com "ver mais" — estilo Trello,
  // igual às tarefas. Guarda os ids expandidos.
  const [expandidos, setExpandidos] = useState<Set<string>>(new Set());
  const toggleExpandir = (cid: string) =>
    setExpandidos((s) => { const n = new Set(s); n.has(cid) ? n.delete(cid) : n.add(cid); return n; });

  const { data: comentarios = [] } = useQuery({
    queryKey: ["projeto-comentarios", projetoId],
    queryFn: () => listarComentarios(projetoId),
  });

  const criarM = useMutation({
    mutationFn: () => criarComentario(projetoId, texto, visivelParaCliente),
    onSuccess: () => {
      setTexto("");
      qc.invalidateQueries({ queryKey: ["projeto-comentarios", projetoId] });
    },
  });

  const deletarM = useMutation({
    mutationFn: (id: string) => deletarComentario(projetoId, id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["projeto-comentarios", projetoId] }),
  });

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-6">
      <h3 className="text-sm font-medium text-slate-700 flex items-center gap-2 mb-4">
        <MessageSquare size={16} /> Comentários do projeto
      </h3>

      <div className="space-y-3 mb-4 max-h-80 overflow-y-auto">
        {comentarios.length === 0 && (
          <p className="text-sm text-slate-400 text-center py-4">Sem comentários ainda.</p>
        )}
        {comentarios.map((c) => {
          const podeRemover = c.autor.id === user?.id || can(["admin"]);
          // "longo" amarra o clamp E o botão ao mesmo gatilho (chars OU muitas linhas),
          // pra nunca cortar sem oferecer "ver mais" (igual às tarefas).
          const longo = c.texto.length > 280 || c.texto.split("\n").length > 6;
          return (
            <div
              key={c.id}
              // Cores SEM opacidade: com `/40` a regra do dark mode (sobrescrita
              // global de classe no index.css) não casa e o balão fica claro no
              // tema escuro, com o texto cinza sumindo dentro. Mesmo bug do
              // AnexoView, jul/2026.
              className={`rounded-lg p-3 border ${c.autor.perfil === "cliente" ? "border-blue-200 bg-blue-50" : "border-slate-200 bg-slate-50"}`}
            >
              <div className="flex items-center gap-2 text-xs">
                <strong className="text-slate-700">{c.autor.nome}</strong>
                <span className="text-[10px] uppercase font-bold px-1.5 py-0.5 rounded bg-slate-200 text-slate-600">{c.autor.perfil}</span>
                {!c.visivelParaCliente && (
                  <span className="text-[10px] uppercase font-bold px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 flex items-center gap-1">
                    <EyeOff size={10} /> interno
                  </span>
                )}
                <span className="ml-auto text-slate-400">{new Date(c.criadoEm).toLocaleString("pt-BR")}</span>
                {podeRemover && (
                  <button
                    onClick={async () => {
                      const ok = await confirm({ title: "Remover comentário", message: "Esta ação não pode ser desfeita.", danger: true, confirmText: "Remover" });
                      if (ok) deletarM.mutate(c.id);
                    }}
                    className="text-slate-400 hover:text-red-500"
                    title="Remover"
                  >
                    <Trash2 size={12} />
                  </button>
                )}
              </div>
              <div className="text-sm text-slate-700 mt-1 whitespace-pre-wrap [overflow-wrap:anywhere]">
                <div className={longo && !expandidos.has(c.id) ? "line-clamp-6" : ""}>
                  {renderTextoRico(c.texto, "underline text-blue-600 hover:text-blue-700")}
                </div>
                {longo && (
                  <button
                    type="button"
                    onClick={() => toggleExpandir(c.id)}
                    className="text-xs text-blue-600 hover:underline mt-1"
                  >
                    {expandidos.has(c.id) ? "ver menos" : "ver mais"}
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <div className="border-t border-slate-100 pt-3">
        <textarea
          value={texto}
          onChange={(e) => setTexto(e.target.value)}
          rows={2}
          placeholder="Escreva um comentário…"
          className="w-full px-3 py-2 rounded-lg border border-slate-300 outline-none text-sm focus:border-blue-500"
        />
        <div className="flex items-center justify-between mt-2 gap-2 flex-wrap">
          <button
            onClick={() => setVisivelParaCliente(!visivelParaCliente)}
            className={`text-xs flex items-center gap-1.5 px-2.5 py-1 rounded ${visivelParaCliente ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}
            title="Alternar visibilidade pro cliente"
          >
            {visivelParaCliente ? <Eye size={12} /> : <EyeOff size={12} />}
            {visivelParaCliente ? "Visível ao cliente" : "Apenas interno"}
          </button>
          <button
            onClick={() => criarM.mutate()}
            disabled={!texto.trim() || criarM.isPending}
            className="text-sm bg-blue-600 hover:bg-blue-700 text-white px-4 py-1.5 rounded flex items-center gap-1.5 disabled:opacity-50"
          >
            <Send size={14} /> Comentar
          </button>
        </div>
      </div>
    </div>
  );
}
